import React, { useState } from 'react';
import { Copy, Check, Download, KeyRound, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

/**
 * Muestra el código de continuidad.
 *
 * Es el momento más delicado de todo el flujo: el código viaja una sola vez y
 * si el paciente no lo guarda, pierde su historial para siempre. Por eso pide
 * una confirmación explícita antes de dejar seguir, y ofrece copiarlo y
 * descargarlo.
 */
export const CodigoContinuidad = ({ codigo, onConfirmar, textoBoton = 'Ya lo guardé, continuar' }) => {
  const [copiado, setCopiado] = useState(false);
  const [confirmado, setConfirmado] = useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(codigo);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Si el navegador no deja copiar, el código está visible en pantalla.
    }
  };

  const descargar = () => {
    const contenido = [
      'AnonimaMente — Código de continuidad',
      '',
      codigo,
      '',
      'Con este código recuperas tus horas y sigues con el mismo profesional,',
      'sin darnos tu nombre ni tu correo.',
      '',
      'Guárdalo en un lugar seguro. Si lo pierdes, no podemos recuperarlo:',
      'no lo tenemos guardado en ninguna parte.',
      '',
      'Para usarlo entra a https://anonimamente.cl/mis-horas',
    ].join('\n');

    const blob = new Blob([contenido], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');

    enlace.href = url;
    enlace.download = 'anonimamente-codigo.txt';
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-6 md:p-8">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center flex-none">
          <KeyRound className="w-5 h-5 text-amber-800" />
        </div>
        <div>
          <h3 className="font-heading text-xl font-semibold text-amber-900">
            Tu código de continuidad
          </h3>
          <p className="text-sm text-amber-800 mt-1">
            Guárdalo ahora. Es lo único que te identifica con nosotros.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-amber-200 p-5 mb-5">
        <p
          className="font-mono text-2xl md:text-3xl font-bold tracking-[0.2em] text-center text-slate-900 select-all break-all"
          data-testid="codigo-continuidad"
        >
          {codigo}
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Button
          type="button"
          variant="outline"
          onClick={copiar}
          className="flex-1 border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
        >
          {copiado ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copiado ? 'Copiado' : 'Copiar código'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={descargar}
          className="flex-1 border-amber-300 bg-white hover:bg-amber-100 text-amber-900"
        >
          <Download className="w-4 h-4 mr-2" />
          Descargar
        </Button>
      </div>

      <div className="flex gap-3 p-4 rounded-xl bg-amber-100/70 mb-6">
        <AlertTriangle className="w-5 h-5 text-amber-700 flex-none mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          No guardamos este código en ninguna parte, solo una huella que no permite
          reconstruirlo. <strong>Si lo pierdes, no podemos recuperarlo</strong> y tendrás
          que empezar de nuevo. Esa es justamente la razón por la que nadie puede
          saber quién eres.
        </p>
      </div>

      {onConfirmar && (
        <>
          <label className="flex items-start gap-3 mb-5 cursor-pointer">
            <Checkbox
              checked={confirmado}
              onCheckedChange={(valor) => setConfirmado(valor === true)}
              className="mt-0.5"
              data-testid="confirmo-codigo"
            />
            <span className="text-sm text-amber-900">
              Guardé mi código en un lugar seguro y entiendo que no se puede recuperar.
            </span>
          </label>

          <Button
            type="button"
            onClick={onConfirmar}
            disabled={!confirmado}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full py-6 disabled:opacity-50"
            data-testid="confirmar-codigo-btn"
          >
            {textoBoton}
          </Button>
        </>
      )}
    </div>
  );
};

export default CodigoContinuidad;
